import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { pickText } from "@heylife/i18n";
import { api, relTime, scopeQuery, useScope, type Localized } from "../api";
import { useT } from "../i18n";
import { Msg, useFlash } from "../ui";

interface Question {
  id: string;
  body: string;
  answer: string | null;
  answeredAt: string | null;
  createdAt: string;
  sessionTitle: Localized | null;
  overdue: boolean;
}

export function AdminQuestions() {
  const { t } = useT();
  const scope = useScope();
  const [status, setStatus] = useState<"open" | "answered">("open");
  const q = useQuery({ queryKey: ["admin", "questions", scope.churchId, status], queryFn: () => api<{ questions: Question[] }>(`/admin/questions${scopeQuery(scope, "church", { status })}`) });

  return (
    <>
      <h1>{t("admin.questions.title")}</h1>
      <p className="muted">{t("admin.questions.hint")}</p>
      <div className="tabs">
        {(["open", "answered"] as const).map((s) => (
          <button key={s} className={s === status ? "active" : ""} onClick={() => setStatus(s)}>
            {t(`admin.questions.${s}`)}
          </button>
        ))}
      </div>
      {q.isError ? <p className="error">{t("admin.common.error")}</p> : null}
      {q.data?.questions.length === 0 ? <p className="muted">{t("admin.questions.none")}</p> : null}
      <div className="stack">
        {q.data?.questions.map((qu) => (
          <QuestionCard key={qu.id} q={qu} />
        ))}
      </div>
    </>
  );
}

function QuestionCard({ q }: { q: Question }) {
  const { t, locale } = useT();
  const qc = useQueryClient();
  const [answer, setAnswer] = useState(q.answer ?? "");
  const [flash, run, busy] = useFlash();
  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (await run(() => api(`/admin/questions/${q.id}/answer`, { method: "POST", json: { answer } }), t("admin.common.saved"))) void qc.invalidateQueries({ queryKey: ["admin", "questions"] });
  };
  return (
    <div className={`card question-card ${q.overdue ? "overdue" : ""}`}>
      <div className="meta muted">
        <span>{q.sessionTitle ? t("admin.questions.session", { title: pickText(q.sessionTitle, locale) }) : t("admin.questions.noSession")}</span>
        <span>{t("admin.questions.asked", { when: relTime(q.createdAt, locale) })}</span>
        {q.overdue ? <span className="pill warn">{t("admin.questions.overdue")}</span> : null}
      </div>
      <p className="body">{q.body}</p>
      {q.answeredAt ? (
        <div className="answer">
          <span className="eyebrow">{t("admin.questions.answer")}</span>
          <p>{q.answer}</p>
          <span className="muted">{t("admin.questions.answeredAt", { when: relTime(q.answeredAt, locale) })}</span>
        </div>
      ) : (
        <form onSubmit={send} className="form">
          <textarea rows={4} required maxLength={4000} value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder={t("admin.questions.answer")} />
          <div className="actions">
            <button className="btn small" disabled={busy || !answer.trim()}>
              {t("admin.questions.send")}
            </button>
          </div>
          <Msg msg={flash} />
        </form>
      )}
    </div>
  );
}
